import { useState, useEffect, useRef, useCallback } from 'react'
import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source'
import { apiOrigin } from '@/lib/config'
import type { LogEntry } from '@/components/log-viewer/LogViewer'

/**
 * Connection state for the SSE stream
 */
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error' | 'complete'

/**
 * Error from the SSE stream
 */
export interface StreamError {
  message: string
  timestamp: Date
}

/**
 * Hook options
 */
export interface UseLogStreamOptions {
  /** Whether to automatically connect */
  enabled?: boolean
  /** Only return logs newer than this many seconds */
  sinceSeconds?: number
  /** Callback when connection state changes */
  onStateChange?: (state: ConnectionState) => void
  /** Callback when stream completes */
  onComplete?: (finalStatus: string) => void
  /** Callback when error occurs */
  onError?: (error: StreamError) => void
}

/**
 * Hook return value
 */
export interface UseLogStreamReturn {
  /** Accumulated log entries */
  logs: LogEntry[]
  /** Current connection state */
  connectionState: ConnectionState
  /** Last error if any */
  error: StreamError | null
  /** Final status when stream completes */
  finalStatus: string | null
  /** Manually connect to the stream */
  connect: () => void
  /** Manually disconnect from the stream */
  disconnect: () => void
  /** Clear accumulated logs */
  clearLogs: () => void
}

/**
 * Hook to stream logs from a running run via Server-Sent Events (SSE).
 * 
 * Connects to `GET /runs/{id}/logs/stream` and accumulates log lines in state.
 * Automatically handles reconnection on transient errors.
 * 
 * @example
 * ```tsx
 * const { logs, connectionState, error } = useLogStream(runId, {
 *   enabled: run.status === 'RUNNING',
 *   onComplete: (status) => console.log('Run finished:', status),
 * })
 * ```
 */
export function useLogStream(
  runId: string,
  options: UseLogStreamOptions = {}
): UseLogStreamReturn {
  const {
    enabled = true,
    sinceSeconds,
    onStateChange,
    onComplete,
    onError,
  } = options

  const [logs, setLogs] = useState<LogEntry[]>([])
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected')
  const [error, setError] = useState<StreamError | null>(null)
  const [finalStatus, setFinalStatus] = useState<string | null>(null)

  const abortControllerRef = useRef<AbortController | null>(null)
  const lineCountRef = useRef(0)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 3

  // Update connection state and notify
  const updateState = useCallback((state: ConnectionState) => {
    setConnectionState(state)
    onStateChange?.(state)
  }, [onStateChange])

  // Parse a log line into a LogEntry
  const parseLogLine = useCallback((line: string): LogEntry => {
    lineCountRef.current += 1
    
    // Try to parse structured log format: [timestamp] [level] message
    const structuredMatch = line.match(/^\[(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}[^\]]*)\]\s*\[(\w+)\]\s*(.*)$/)
    if (structuredMatch) {
      const [, timestamp, level, content] = structuredMatch
      return {
        lineNumber: lineCountRef.current,
        timestamp,
        level: level.toLowerCase() as LogEntry['level'],
        content,
      }
    }

    // Try to parse ISO timestamp prefix: 2024-01-01T00:00:00Z message
    const isoMatch = line.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[^\s]*)\s+(.*)$/)
    if (isoMatch) {
      return {
        lineNumber: lineCountRef.current,
        timestamp: isoMatch[1],
        content: isoMatch[2],
      }
    }

    // Plain text line
    return {
      lineNumber: lineCountRef.current,
      content: line,
    }
  }, [])

  // Disconnect from the stream
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    updateState('disconnected')
  }, [updateState])

  // Connect to the stream
  const connect = useCallback(async () => {
    // Disconnect existing connection
    disconnect()

    // Build URL - use orchestrator URL with /api prefix (YARP proxy)
    // This routes through orchestrator which handles auth and proxies to agentic-server
    const orchestratorUrl = apiOrigin()
    let url = `${orchestratorUrl}/api/runs/${runId}/logs/stream`
    if (sinceSeconds) {
      url += `?since_seconds=${sinceSeconds}`
    }

    updateState('connecting')
    setError(null)

    try {
      const controller = new AbortController()
      abortControllerRef.current = controller

      // `credentials: 'include'` and no Authorization header: the stream is authenticated by the session
      // cookie like every other request. Native EventSource still cannot be given options, which is why
      // this is fetchEventSource — it was already, for the header it no longer needs.
      await fetchEventSource(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': EventStreamContentType,
        },
        signal: controller.signal,
        openWhenHidden: true,

        async onopen(response) {
          if (response.ok && response.headers.get('content-type')?.includes(EventStreamContentType)) {
            updateState('connected')
            reconnectAttempts.current = 0
          } else {
            throw new Error(`Unexpected response: ${response.status} ${response.statusText}`)
          }
        },

        onmessage(msg) {
          switch (msg.event) {
            case 'message':
            case '': {
              // Default message event contains log lines
              const logEntry = parseLogLine(msg.data)
              setLogs((prev) => [...prev, logEntry])
              break
            }
            case 'heartbeat':
              // Keep-alive, no action
              break
            case 'waiting': {
              try {
                const data = JSON.parse(msg.data)
                const logEntry: LogEntry = {
                  lineNumber: lineCountRef.current + 1,
                  content: `[System] ${data.message}`,
                  level: 'info',
                }
                lineCountRef.current += 1
                setLogs((prev) => [...prev, logEntry])
              } catch {
                // Ignore parse errors
              }
              break
            }
            case 'complete': {
              try {
                const data = JSON.parse(msg.data)
                setFinalStatus(data.status)
                onComplete?.(data.status)
              } catch {
                setFinalStatus('unknown')
                onComplete?.('unknown')
              }
              disconnect()
              updateState('complete')
              break
            }
            case 'error': {
              try {
                const data = JSON.parse(msg.data)
                const streamError: StreamError = {
                  message: data.error || 'Unknown error',
                  timestamp: new Date(),
                }
                setError(streamError)
                onError?.(streamError)
              } catch {
                // Parse failed
              }
              break
            }
          }
        },

        onerror(err) {
          // Connection error - attempt reconnect
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current += 1
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000)
            
            updateState('connecting')
            reconnectTimeoutRef.current = setTimeout(() => {
              connect()
            }, delay)
          } else {
            const streamError: StreamError = {
              message: 'Connection failed after multiple attempts',
              timestamp: new Date(),
            }
            setError(streamError)
            onError?.(streamError)
            updateState('error')
          }
          // Throw to stop fetchEventSource's built-in retry
          throw err
        },
      })
    } catch (err) {
      // AbortError is normal disconnect, ignore
      if (err instanceof DOMException && err.name === 'AbortError') {
        return
      }
      // Errors re-thrown from onerror are already handled there, skip
      if (reconnectAttempts.current > 0) {
        return
      }
      const streamError: StreamError = {
        message: err instanceof Error ? err.message : 'Failed to connect',
        timestamp: new Date(),
      }
      setError(streamError)
      onError?.(streamError)
      updateState('error')
    }
  }, [runId, sinceSeconds, disconnect, updateState, parseLogLine, onComplete, onError])

  // Clear logs
  const clearLogs = useCallback(() => {
    setLogs([])
    lineCountRef.current = 0
  }, [])

  // Auto-connect when enabled
  useEffect(() => {
    if (enabled && runId) {
      connect()
    } else {
      disconnect()
    }

    return () => {
      disconnect()
    }
  }, [enabled, runId]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    logs,
    connectionState,
    error,
    finalStatus,
    connect,
    disconnect,
    clearLogs,
  }
}
